import style from "./style.module.css";
import type {
  ComponentType,
  MouseEvent,
  MouseEventHandler,
  ReactNode
} from "react";
import type { IconProps } from "views/components/icons/types";
import Button from "views/components/button";
import NoComponent from "views/components/NoComponent";
import EllipsisIcon from "views/components/icons/EllipsisIcon";
import Logger from "lib/logger";
import { noop } from "lib/lodash";
import composeClassName from "lib/composeClassName";
import useMobile from "views/components/Editor/mobile/useMobile";
import MobileDesignActionSheet from "views/containers/DesignsPageContainer/Workspace/mobile/MobileDesignActionSheet";

interface IconButtonProps {
  /** Rendered inside the button; receives colour and size. */
  Icon?: ComponentType<IconProps>;
  iconColor?: string;
  iconSize?: string;
  /** Square box around the icon in px; defaults to iconSize. */
  iconBoxSize?: string;
  iconClassName?: string;
  title?: ReactNode;
  titleClassName?: string;
  className?: string;
  wrapperClassName?: string;
  theme?: string;
  isDisabled?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  /** Rendered when showMenu is set; an ellipsis toggle appears when given. */
  MenuComponent?: ReactNode;
  showMenu?: boolean;
  setMenuState?: (isOpen: boolean) => void;
}

const IconButton = ({
  iconColor = "#9a9b9d",
  Icon = NoComponent,
  title,
  className,
  iconSize = "20",
  iconBoxSize,
  onClick = noop,
  MenuComponent,
  showMenu,
  setMenuState,
  titleClassName,
  isDisabled,
  wrapperClassName,
  theme,
  iconClassName
}: IconButtonProps) => {
  const isMobile = useMobile();
  const showMobileUI = isMobile;

  if (!iconBoxSize) iconBoxSize = iconSize;
  if (!Icon) {
    Logger.error(
      "No Icon Provided. Please provide an Icon or use Button Component"
    );
    return null;
  }

  const getButtonClassName = () => {
    const classes = [style.button];

    if (className) {
      classes.push(className);
    }

    return classes.join(" ");
  };

  const onToggleMenuClick = (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    setMenuState?.(!showMenu);
  };

  return (
    <div
      className={[style.wrapper, wrapperClassName].filter(x => x).join(" ")}
      data-testid="IconButton"
    >
      <Button
        className={getButtonClassName()}
        onClick={onClick}
        disabled={isDisabled}
        theme={theme}
      >
        <div
          className={`${style.icon} ${iconClassName}`}
          style={{ width: `${iconBoxSize}px`, height: `${iconBoxSize}px` }}
        >
          <Icon color={iconColor} size={iconSize} />
        </div>
        {title && (
          <div
            className={composeClassName("title", style, titleClassName)}
            data-testid="iconButtonTitle"
          >
            {title}
          </div>
        )}
        {MenuComponent && (
          <div
            className={style.menu}
            data-is-active={showMenu}
            onClick={onToggleMenuClick}
          >
            <EllipsisIcon
              color="#999c9e"
              withBackground={true}
              backgroundColor="transparent"
            />
          </div>
        )}
      </Button>
      {showMenu && !showMobileUI && MenuComponent}
      {showMenu && showMobileUI && (
        <MobileDesignActionSheet
          isOpen={showMenu}
          onClose={() => setMenuState?.(false)}
        >
          {MenuComponent}
        </MobileDesignActionSheet>
      )}
    </div>
  );
};

IconButton.displayName = "IconButton";

export default IconButton;
